/**
 * Original Adventure benchmark cast. All meshes are authored here; no downloaded
 * art or physics. +Z is forward and y=0 is the contact plane. Pose is cosmetic:
 * the caller alone owns root position/yaw and supplies simulation time/speed.
 */
import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { CreateSphere } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { CreateCylinder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { CreateTorus } from '@babylonjs/core/Meshes/Builders/torusBuilder';
import { CreateTube } from '@babylonjs/core/Meshes/Builders/tubeBuilder';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';

export interface AdventureActor {
  root: TransformNode;
  meshes: Mesh[];
  update(timeS: number, speedMps: number): void;
  dispose(): void;
}
type Triple = readonly [number, number, number];
type Ring = readonly [y: number, rx: number, rz: number, cz?: number];

/** Rounded cross-section loft, with real changes in silhouette along its length. */
function loft(name: string, scene: Scene, rings: readonly Ring[], power = 2, segments = 32): Mesh {
  const positions: number[] = [], indices: number[] = [], uvs: number[] = [];
  const start = rings[0]![0], height = rings[rings.length - 1]![0] - start;
  for (const [y, rx, rz, cz = 0] of rings) {
    for (let i = 0; i <= segments; i++) {
      const a = i / segments * Math.PI * 2;
      const c = Math.cos(a), s = Math.sin(a);
      positions.push(Math.sign(c) * Math.pow(Math.abs(c), 2 / power) * rx, y,
        Math.sign(s) * Math.pow(Math.abs(s), 2 / power) * rz + cz);
      uvs.push(i / segments, (y - start) / Math.max(height, .001));
    }
  }
  for (let row = 0; row < rings.length - 1; row++) for (let i = 0; i < segments; i++) {
    const a = row * (segments + 1) + i, b = a + segments + 1;
    indices.push(a, a + 1, b, a + 1, b + 1, b);
  }
  // End caps use their own centre vertex; cap radii may be small but never zero.
  for (const [row, reverse] of [[0, true], [rings.length - 1, false]] as const) {
    const ring = rings[row]!, centre = positions.length / 3;
    positions.push(0, ring[0], ring[3] ?? 0); uvs.push(.5, reverse ? 0 : 1);
    for (let i = 0; i < segments; i++) {
      const a = row * (segments + 1) + i;
      if (reverse) indices.push(centre, a + 1, a); else indices.push(centre, a, a + 1);
    }
  }
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  // Join duplicate seam normals to remove the otherwise visible vertical seam.
  for (let r = 0; r < rings.length; r++) {
    const a = r * (segments + 1) * 3, b = (r * (segments + 1) + segments) * 3;
    const n = new Vector3(normals[a]! + normals[b]!, normals[a + 1]! + normals[b + 1]!, normals[a + 2]! + normals[b + 2]!).normalize();
    for (const o of [a, b]) { normals[o] = n.x; normals[o + 1] = n.y; normals[o + 2] = n.z; }
  }
  const data = new VertexData(); data.positions = positions; data.normals = normals; data.uvs = uvs; data.indices = indices;
  const mesh = new Mesh(name, scene); data.applyToMesh(mesh); return mesh;
}

/** A continuous garment/skin surface bends through the joint instead of exposing
 * two rigid tube caps. The small buffer is updated only when its bend changes. */
function continuousBend(mesh: Mesh, pivotY: number, span: number): (angle: number) => void {
  const source = Float32Array.from(mesh.getVerticesData('position')!);
  const sourceNormals = Float32Array.from(mesh.getVerticesData('normal')!);
  const vertices = new Float32Array(source), normals = new Float32Array(sourceNormals);
  mesh.setVerticesData('position', vertices, true); mesh.setVerticesData('normal', normals, true);
  let previous = Infinity;
  return angle => {
    if (Math.abs(angle - previous) < .0001) return;
    previous = angle;
    for (let i = 0; i < source.length; i += 3) {
      const y = source[i + 1]!, z = source[i + 2]!;
      const amount = Math.max(0, Math.min(1, (pivotY + span / 2 - y) / span));
      const bend = angle * amount * amount * (3 - 2 * amount);
      const c = Math.cos(bend), q = Math.sin(bend), dy = y - pivotY;
      vertices[i + 1] = pivotY + dy * c - z * q; vertices[i + 2] = dy * q + z * c;
      normals[i + 1] = sourceNormals[i + 1]! * c - sourceNormals[i + 2]! * q;
      normals[i + 2] = sourceNormals[i + 1]! * q + sourceNormals[i + 2]! * c;
    }
    mesh.updateVerticesData('position', vertices, true); mesh.updateVerticesData('normal', normals);
  };
}

class Sculpt {
  readonly root: TransformNode;
  private readonly parts: Mesh[] = [];
  private readonly materials = new Map<string, PBRMaterial>();
  constructor(readonly scene: Scene, name: string) { this.root = new TransformNode(name, scene); }
  material(name: string, hex: string, roughness = .6, metallic = 0): PBRMaterial {
    const existing = this.materials.get(name); if (existing) return existing;
    const mat = new PBRMaterial(`${this.root.name}_${name}`, this.scene);
    mat.albedoColor = Color3.FromHexString(hex); mat.roughness = roughness; mat.metallic = metallic;
    mat.environmentIntensity = .8;
    this.materials.set(name, mat); return mat;
  }
  joint(name: string, at: Triple, parent = this.root): TransformNode {
    const joint = new TransformNode(`${this.root.name}_${name}`, this.scene); joint.parent = parent; joint.position.set(...at); return joint;
  }
  add(mesh: Mesh, mat: PBRMaterial, at: Triple = [0, 0, 0], parent = this.root): Mesh {
    mesh.material = mat; mesh.parent = parent; mesh.position.set(...at); mesh.isPickable = false; mesh.receiveShadows = true;
    this.parts.push(mesh); return mesh;
  }
  form(name: string, rings: readonly Ring[], mat: PBRMaterial, at: Triple = [0, 0, 0], parent = this.root, power = 2, segments = 32): Mesh {
    return this.add(loft(name, this.scene, rings, power, segments), mat, at, parent);
  }
  oval(name: string, size: Triple, at: Triple, mat: PBRMaterial, parent = this.root, segments = 14): Mesh {
    const mesh = CreateSphere(name, { diameter: 1, segments }, this.scene); mesh.scaling.set(...size); return this.add(mesh, mat, at, parent);
  }
  block(name: string, size: Triple, at: Triple, mat: PBRMaterial, parent = this.root, bevel = .05): Mesh {
    const [w, h, d] = size, b = Math.min(bevel, h * .3, w * .2, d * .2);
    return this.form(name, [[-h / 2, w / 2 - b, d / 2 - b], [-h / 2 + b * .3, w / 2 - b * .3, d / 2 - b * .3], [-h / 2 + b, w / 2, d / 2], [h / 2 - b, w / 2, d / 2], [h / 2 - b * .3, w / 2 - b * .3, d / 2 - b * .3], [h / 2, w / 2 - b, d / 2 - b]], mat, at, parent, 5, 24);
  }
  rod(name: string, from: Triple, to: Triple, r1: number, r2: number, mat: PBRMaterial, parent = this.root): Mesh {
    const a = new Vector3(...from), b = new Vector3(...to), delta = b.subtract(a);
    const mesh = CreateCylinder(name, { height: delta.length(), diameterBottom: r1 * 2, diameterTop: r2 * 2, tessellation: 16 }, this.scene);
    this.add(mesh, mat, [0, 0, 0], parent); mesh.position.copyFrom(a.add(b).scale(.5));
    mesh.rotationQuaternion = Quaternion.FromUnitVectorsToRef(Vector3.Up(), delta.normalize(), new Quaternion()); return mesh;
  }
  curve(name: string, path: Triple[], radius: number, mat: PBRMaterial, parent = this.root, tessellation = 8): Mesh {
    return this.add(CreateTube(name, { path: path.map(p => new Vector3(...p)), radius, tessellation, cap: Mesh.CAP_ALL }, this.scene), mat, [0, 0, 0], parent);
  }
  finish(update: AdventureActor['update']): AdventureActor {
    const buckets = new Map<TransformNode, Map<PBRMaterial, Mesh[]>>();
    for (const part of this.parts) {
      const parent = part.parent as TransformNode, material = part.material as PBRMaterial;
      let byMat = buckets.get(parent); if (!byMat) { byMat = new Map(); buckets.set(parent, byMat); }
      const group = byMat.get(material) ?? []; group.push(part); byMat.set(material, group);
    }
    const meshes: Mesh[] = [];
    for (const [parent, groups] of buckets) for (const [material, parts] of groups) {
      if (parts.length === 1) { meshes.push(parts[0]!); continue; }
      parent.computeWorldMatrix(true);
      const merged = Mesh.MergeMeshes(parts, true, true, undefined, false, false);
      if (merged) { merged.name = `${parent.name}_${material.name}_batch`; merged.setParent(parent); merged.isPickable = false; merged.receiveShadows = true; meshes.push(merged); }
    }
    return { root: this.root, meshes, update, dispose: () => { this.root.dispose(false); for (const mat of this.materials.values()) mat.dispose(false, true); } };
  }
}

/** Hand-drawn package graphics. Separate from any UI or study telemetry. */
function packagePrint(s: Sculpt): PBRMaterial {
  const mat = s.material('packagePrint', '#FFFFFF', .89);
  if (typeof document === 'undefined') { mat.albedoColor = Color3.FromHexString('#F8D7B2'); return mat; }
  const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#bd421f'; ctx.fillRect(0, 0, 512, 512);
  ctx.strokeStyle = '#e38245'; ctx.lineWidth = 4; ctx.strokeRect(18, 18, 476, 476);
  ctx.fillStyle = '#fff4db'; ctx.textAlign = 'center'; ctx.font = 'bold 82px Arial'; ctx.fillText('PHỞ', 256, 113); ctx.fillText('NGON', 256, 204);
  ctx.beginPath(); ctx.ellipse(256, 290, 101, 25, 0, 0, Math.PI * 2); ctx.fillStyle = '#ede0bf'; ctx.fill();
  ctx.beginPath(); ctx.arc(256, 290, 100, 0, Math.PI); ctx.fillStyle = '#f7f0d9'; ctx.fill();
  ctx.beginPath(); ctx.ellipse(256, 290, 91, 18, 0, 0, Math.PI * 2); ctx.fillStyle = '#bc963a'; ctx.fill();
  for (let i = 0; i < 9; i++) { ctx.strokeStyle = i % 2 ? '#28703a' : '#ead89c'; ctx.lineWidth = 7; ctx.beginPath(); ctx.moveTo(189 + i * 14, 283); ctx.quadraticCurveTo(192 + i * 12, 295, 202 + i * 11, 289); ctx.stroke(); }
  ctx.strokeStyle = '#744923'; ctx.lineWidth = 7;
  for (const x of [274, 292]) { ctx.beginPath(); ctx.moveTo(x, 302); ctx.lineTo(x + 20, 223); ctx.stroke(); }
  ctx.fillStyle = '#ffe7c6'; ctx.font = '24px Arial'; ctx.fillText('GIAO TẬN NƠI', 256, 436);
  mat.albedoTexture = new DynamicTexture('adventureOriginalPackageArt', canvas, s.scene, true, Texture.TRILINEAR_SAMPLINGMODE);
  mat.albedoTexture.hasAlpha = false; (mat.albedoTexture as DynamicTexture).update(true); return mat;
}

export function buildAdventureRobot(scene: Scene): AdventureActor {
  const s = new Sculpt(scene, 'adventureHero');
  const ivory = s.material('ivoryCeramic', '#E2DED0', .31);
  const pearl = s.material('pearlPanels', '#F2EEDF', .26);
  const rubber = s.material('rubber', '#141B1B', .94);
  const seam = s.material('panelSeams', '#566363', .6);
  const metal = s.material('brushedAluminium', '#949F9C', .3, .78);
  const darkGlass = s.material('smokedGlass', '#0A1519', .18, .15);
  const bag = s.material('wovenCarrier', '#C75029', .9);
  const edge = s.material('orangePiping', '#E57C43', .76);
  const cyan = s.material('statusCyan', '#30E3D3', .25); cyan.emissiveColor = Color3.FromHexString('#087F79');
  const tailPivot = s.joint('tail', [0, .25, -.388]);
  const wheels: TransformNode[] = [];
  const body = s.joint('body', [0, .0255, 0]);
  // The fitting pass adjusts only X/Z. Author the body squat enough that it
  // remains a delivery robot, with circular wheels, inside the .6m footprint.
  body.scaling.y = .85;
  // A low wheelbase and separate sculpted shoulder housing, with inset joints.
  s.form('undercarriage', [[.115,.215,.325],[.14,.26,.37],[.25,.273,.363],[.285,.248,.345]], rubber, [0,0,0]);
  s.form('lowerShell', [[.17,.219,.307],[.205,.273,.35],[.30,.297,.36],[.38,.285,.33],[.435,.249,.275],[.45,.224,.254]], ivory, [0,0,0], body, 3.2, 48);
  s.form('waistGasket', [[.435,.222,.24],[.48,.23,.234],[.5,.221,.225]], seam, [0,0,0], body, 3.5);
  s.form('upperHousing', [[.475,.208,.209],[.50,.251,.231],[.55,.274,.25],[.74,.27,.236],[.79,.24,.217],[.815,.202,.18]], pearl, [0,0,0], body, 3.8, 48);
  for (const side of [-1,1]) {
    s.form('sideFender', [[.17,.054,.27],[.25,.06,.30],[.34,.065,.26],[.37,.038,.21]], ivory, [side*.247,0,0], body, 2.8);
    for (const z of [-.245,.245]) {
      const wheel = s.joint('wheel', [side * .272, .143, z]);
      wheels.push(wheel);
      const tire = CreateTorus('roundedPneumaticTire', { diameter:.198, thickness:.082, tessellation:28 }, scene); tire.rotation.z = Math.PI/2; s.add(tire,rubber,[0,0,0],wheel);
      const hub = CreateCylinder('recessedHub', { diameter:.176, height:.08, tessellation:32 }, scene); hub.rotation.z = Math.PI/2; s.add(hub,metal,[0,0,0],wheel);
      const rim = CreateTorus('rimLip', {diameter:.182, thickness:.013,tessellation:32},scene); rim.rotation.z = Math.PI/2; s.add(rim,seam,[side*.046,0,0],wheel);
      for (let spoke=0;spoke<5;spoke++) { const a=spoke/5*Math.PI*2; s.rod('hubSpoke',[side*.046,Math.cos(a)*.035,Math.sin(a)*.035],[side*.046,Math.cos(a)*.072,Math.sin(a)*.072],.009,.006,ivory,wheel); }
    }
    s.oval('shoulderSocket',[.102,.112,.103],[side*.244,.691,0],rubber,body);
    s.curve('carrierArm',[[side*.265,.688,-.005],[side*.317,.729,-.023],[side*.338,.849,-.025],[side*.306,.925,-.025]],.034,metal,body,12);
    s.oval('armSleeve',[.078,.116,.084],[side*.329,.826,-.025],ivory,body);
    s.oval('carrierGrip',[.069,.122,.083],[side*.299,.946,-.025],pearl,body);
    for (let f=0;f<3;f++) s.curve('gripFinger',[[side*.288,.955-f*.026,-.059],[side*.271,.958-f*.026,-.074],[side*.26,.95-f*.026,-.071]],.01,rubber,body);
  }
  // The rear readout is visible from the chase camera; the front retains sensors.
  for (const direction of [-1,1]) {
    const panel=s.block('insetScreen',[.411,.187,.037],[0,.662,direction*.239],darkGlass,body,.03);
    panel.rotation.x=direction*-.04;
    s.block('screenGasket',[.431,.209,.022],[0,.661,direction*.231],seam,body,.03);
    s.block('statusStrip',[.064,.006,.005],[0,.775,direction*.217],cyan,body,.001);
    if (direction===1) for(const x of [-.075,.075]) s.block('screenEye',[.025,.058,.005],[x,.675,.26],cyan,body,.004);
    for (const x of [-.157,.157]) s.oval('bumperUltrasonic',[.043,.043,.016],[x,.31,direction*.35],darkGlass,body,14);
  }
  s.oval('tailRoot',[.071,.077,.049],[0,0,0],seam,tailPivot);
  s.oval('tailNub',[.091,.106,.091],[0,-.013,-.037],pearl,tailPivot);
  const carrier=s.joint('carrier',[0,.995,-.018],body);
  s.block('insulatedCarrier',[.565,.356,.46],[0,0,0],bag,carrier,.035);
  s.block('carrierBase',[.558,.036,.454],[0,-.17,0],edge,carrier,.012);
  s.block('softTopFlap',[.584,.053,.48],[0,.166,0],edge,carrier,.014);
  s.block('flapInner',[.567,.015,.464],[0,.19,0],bag,carrier,.005);
  for(const x of [-.242,.242]) for(const z of [-.19,.19]) s.curve('stitchedCorner',[[x,-.155,z],[x,-.07,z*1.11],[x,.07,z*1.13],[x,.153,z]],.004,edge,carrier,6);
  for(const side of [-1,1]) {
    s.curve('bagSideHandle',[[side*.285,.10,-.087],[side*.309,.09,-.065],[side*.314,.024,-.055],[side*.309,.01,.045],[side*.285,.045,.066]],.012,rubber,carrier);
    for(const x of [-.177,.177]) s.block('buckle',[.032,.044,.009],[x,.139,side*.239],metal,carrier,.004);
  }
  const print=packagePrint(s);
  for(const side of [-1,1]) { const label=CreatePlane('originalDeliveryPrint',{width:.275,height:.263},scene); if(side===1) label.rotation.y=Math.PI; s.add(label,print,[0,-.012,side*.231],carrier); }
  const top=CreatePlane('topDeliveryPrint',{width:.286,height:.271},scene); top.rotation.x=Math.PI/2; s.add(top,print,[0,.199,0],carrier);
  let lastTime: number | undefined, travelled = 0;
  return s.finish((time,speed)=>{ const dt=lastTime===undefined?0:Math.max(0,Math.min(.1,time-lastTime));lastTime=time;travelled+=dt*speed;
    for(const wheel of wheels)wheel.rotation.x=travelled/.143;
    const move=Math.min(Math.abs(speed)/2,1); body.rotation.x=Math.sin(travelled*6)*.003*move; carrier.rotation.z=Math.sin(travelled*4)*.003*move; tailPivot.rotation.z=Math.sin(time*5)*.055*move; });
}

export interface AdventurePedestrianOptions { palette?: number; carryingBag?: boolean }
export function buildAdventurePedestrian(scene: Scene, options: AdventurePedestrianOptions = {}): AdventureActor {
  const s = new Sculpt(scene,'adventurePedestrian');
  const skin=s.material('skin','#CDA27F',.7), lips=s.material('lips','#9D6250',.68);
  const hair=s.material('hair','#282520',.48), eye=s.material('eyes','#272220',.34);
  const shirt=s.material('cottonShirt',options.palette===1?'#6F8275':'#D5C7AF',.93);
  const shirtSeam=s.material('shirtSeams',options.palette===1?'#64786C':'#C4B69F',.95);
  const denim=s.material('denim','#465E70',.96), denimSeam=s.material('denimSeam','#526879',.96);
  const shoe=s.material('canvasShoes','#D3C3AA',.87), sole=s.material('rubberSoles','#AFA38C',.95);
  const bagMat=s.material('marketBag','#7A9156',.91), trim=s.material('bagTrim','#C2B583',.91);
  const torso=s.joint('torso',[0,0,0]);
  // Tailored shirt: neck hole, sloped shoulders, waist pinch and loose rolled hem.
  s.form('shirtBody',[[.963,.133,.094,-.005],[.988,.145,.101,-.006],[1.037,.155,.107,-.005],[1.09,.15,.105],[1.18,.168,.116,.009],[1.28,.174,.111,.007],[1.34,.177,.091],[1.376,.148,.078],[1.41,.092,.062],[1.42,.058,.057]],shirt,[0,0,0],torso,2.3,40);
  s.form('neck',[[1.399,.048,.045],[1.46,.047,.043],[1.501,.056,.047]],skin,[0,0,0],torso,2,24);
  s.form('shirtCollar',[[1.411,.06,.057],[1.419,.062,.061],[1.426,.057,.056]],shirtSeam,[0,0,0],torso,2,32);
  const head=s.joint('head',[0,1.598,0],torso);
  head.scaling.set(1.1,1.07,1.08);
  s.form('face',[[ -.112,.033,.037,.017],[-.087,.057,.060,.015],[-.035,.087,.081,.003],[.026,.093,.085],[.077,.086,.078,-.005],[.108,.058,.056,-.006],[.12,.018,.023,-.009]],skin,[0,0,0],head,2,48);
  s.oval('nose',[.023,.041,.026],[0,-.026,.083],skin,head,14);
  for(const side of [-1,1]) {
    s.oval('ear',[.030,.056,.027],[side*.09,-.025,0],skin,head,12);
    s.oval('eyelid',[.04,.015,.009],[side*.037,.017,.082],lips,head,12);
    s.oval('eye',[.026,.009,.008],[side*.037,.018,.087],eye,head,12);
    s.curve('eyebrow',[[side*.017,.040,.082],[side*.037,.044,.080],[side*.055,.039,.074]],.0038,hair,head);
  }
  s.curve('mouth',[[-.02,-.066,.071],[0,-.069,.075],[.02,-.066,.071]],.0028,lips,head);
  const mask=s.material('cottonFaceMask','#DEDCCF',.96);
  s.form('faceMask',[[-.089,.034,.014,.064],[-.078,.054,.024,.070],[-.046,.068,.029,.075],[-.019,.047,.019,.081],[-.011,.022,.01,.088]],mask,[0,0,0],head,2.5,32);
  for(const side of [-1,1])s.curve('maskElastic',[[side*.061,-.023,.085],[side*.091,-.004,.023],[side*.096,-.04,.012],[side*.058,-.076,.076]],.0023,mask,head,5);
  // A fitted scalp patch has a raised front hairline and a lower rear edge.
  const positions:number[]=[],indices:number[]=[],uvs:number[]=[]; const rows=12,cols=48;
  for(let j=0;j<=rows;j++) for(let i=0;i<=cols;i++) {
    const a=i/cols*Math.PI*2, front=(Math.sin(a)+1)/2, end=2.03-front*.97;
    const lat=.025+j/rows*end; positions.push(Math.cos(a)*Math.sin(lat)*.098,Math.cos(lat)*.139,Math.sin(a)*Math.sin(lat)*.097-.007); uvs.push(i/cols,j/rows);
  }
  for(let j=0;j<rows;j++) for(let i=0;i<cols;i++){const a=j*(cols+1)+i,b=a+cols+1;indices.push(a,b,a+1,a+1,b,b+1);}
  const normals:number[]=[];VertexData.ComputeNormals(positions,indices,normals);
  const data=new VertexData();data.positions=positions;data.normals=normals;data.indices=indices;data.uvs=uvs;const scalp=new Mesh('sculptedHairline',scene);data.applyToMesh(scalp);s.add(scalp,hair,[0,0,0],head);
  s.oval('hairBun',[.099,.096,.096],[0,.025,-.114],hair,head,18);
  for(let i=0;i<7;i++){ const x=-.062+i*.019;s.curve('hairStrand',[[x,.093,.029],[x*.7,.133,.002],[x*.55,.10,-.07],[x*.32,.036,-.099]],.0012,s.material('hairHighlights','#3C3329',.58),head,5); }
  s.form('trouserWaist',[[.845,.046,.048],[.88,.112,.075],[.94,.152,.09],[.986,.151,.098]],denim,[0,0,0],torso,2.5);
  const hips: TransformNode[] = [], knees: TransformNode[] = [], ankles: TransformNode[] = [];
  const shoulders: TransformNode[] = [], elbows: TransformNode[] = [];
  const bendLegs: ((angle: number) => void)[] = [], bendArms: ((angle: number) => void)[] = [];
  for (const side of [-1, 1]) {
    const hip = s.joint('hip', [side * .081, .921, 0]); hips.push(hip);
    const garment = s.joint('continuousTrouser', [0, 0, 0], hip);
    const leg = s.form('continuousDenimLeg', [
      [-.824,.059,.057],[-.803,.067,.061],[-.765,.07,.064],[-.68,.067,.065],
      [-.59,.073,.071],[-.50,.075,.071],[-.445,.071,.068],[-.40,.07,.068],
      [-.355,.073,.073],[-.29,.082,.08],[-.2,.089,.087],[-.09,.095,.088],
      [.015,.089,.083],[.055,.072,.072],
    ], denim, [0,0,0], garment, 2.12, 32);
    bendLegs.push(continuousBend(leg, -.416, .19));
    const knee = s.joint('knee', [0,-.416,0], hip); knees.push(knee);
    const ankle = s.joint('ankle', [0,-.406,0], knee); ankles.push(ankle);
    s.form('shoeSole', [[-.082,.063,.108,.034],[-.059,.065,.116,.034],[-.05,.063,.109,.035]], sole, [0,0,0], ankle, 3);
    s.form('shoeUpper', [[-.055,.061,.109,.035],[-.01,.056,.1,.035],[.032,.041,.066,.004],[.049,.029,.037,-.013]], shoe, [0,0,0], ankle, 2.5);
    for (let lace=0;lace<3;lace++) s.curve('shoeLace', [[-.029,.003-lace*.006,.024+lace*.018],[0,.011-lace*.006,.028+lace*.018],[.029,.003-lace*.006,.024+lace*.018]], .0015, s.material('laces','#E2D5BA',.96), ankle, 5);
    const shoulder = s.joint('shoulder', [side*.164,1.351,-.006], torso); shoulders.push(shoulder);
    shoulder.rotation.z = side*.115;
    // The sleeve crown meets the sloping shoulder under the torso, rather than
    // ending in an exposed horizontal cap at the top of each arm.
    s.form('drapedSleeve', [[-.153,.049,.047],[-.142,.052,.05],[-.09,.06,.058],[-.025,.065,.060],[.012,.052,.049],[.037,.026,.03],[.042,.009,.012]], shirt, [0,0,0], shoulder, 2, 32);
    s.form('softSleeveHem', [[-.154,.049,.047],[-.148,.052,.05],[-.141,.052,.05]], shirtSeam, [0,0,0], shoulder, 2);
    const skinRoot = s.joint('continuousArm', [0,0,0], shoulder);
    const arm = s.form('continuousArmAndHand', [
      [-.578,.009,.009,.008],[-.57,.021,.012,.007],[-.541,.027,.018,.004],
      [-.507,.025,.021,.002],[-.481,.021,.023],[-.451,.025,.028],
      [-.39,.032,.033],[-.32,.038,.035],[-.272,.036,.034],
      [-.235,.036,.034],[-.20,.041,.039],[-.13,.047,.044],
      [-.055,.05,.047],[.01,.043,.041],
    ], skin, [0,0,0], skinRoot, 2, 28);
    bendArms.push(continuousBend(arm, -.247, .13));
    const elbow = s.joint('elbow',[0,-.247,0],shoulder); elbows.push(elbow);
    // One subtle thumb has an anatomical overlap with the palm; individual
    // tiny finger spheres were reading as exposed mechanical wrist joints.
    s.oval('restingThumb',[.018,.037,.021],[side*.024,-.283,.011],skin,elbow,10);
  }
  let shopping:TransformNode|undefined;
  if(options.carryingBag!==false){shopping=s.joint('shoppingBag',[.0,-.375,.016],elbows[1]!);
    s.form('marketBagBody',[[-.20,.063,.047],[-.17,.10,.056],[-.045,.095,.055],[.012,.07,.038]],bagMat,[0,0,0],shopping,2.6);
    for(const z of [-.02,.02])s.curve('bagHandle',[[-.05,-.015,z],[-.04,.081,z],[0,.098,z],[.04,.081,z],[.05,-.015,z]],.006,trim,shopping);
    for(const x of [-.059,.0,.058])s.curve('bagFold',[[x,-.17,.055],[x*.8,-.08,.057],[x*.7,-.01,.04]],.0025,trim,shopping,5);
  }
  let lastTime: number | undefined, phase = 0, activity = 0;
  return s.finish((time,speed)=>{
    const dt=lastTime===undefined?0:Math.max(0,Math.min(.1,time-lastTime));lastTime=time;
    phase+=dt*Math.abs(speed)*6;
    activity+=(Math.min(Math.abs(speed)/1.05,1)-activity)*(1-Math.exp(-dt*12));
    torso.position.y=Math.abs(Math.sin(phase))*.017*activity; torso.rotation.y=Math.sin(phase)*.035*activity;
    head.rotation.y=Math.sin(time*.7)*.025; head.rotation.z=Math.sin(phase)*.012*activity;
    for(let i=0;i<2;i++) {const p=phase+i*Math.PI,swing=Math.sin(p),hip=hips[i]!,knee=knees[i]!,ankle=ankles[i]!;
      hip.rotation.x=swing*.34*activity; knee.rotation.x=-Math.max(0,-swing)*.48*activity; ankle.rotation.x=Math.max(0,swing)*.10*activity;
      bendLegs[i]!(knee.rotation.x);
      shoulders[i]!.rotation.x=-swing*.2*activity; elbows[i]!.rotation.x=-.12-Math.max(0,swing)*.12*activity;
      bendArms[i]!(elbows[i]!.rotation.x);
    }
    if(shopping) shopping.rotation.x=Math.sin(phase)*.13*activity;
  });
}

/** Original step-through city scooter, optionally carrying a seated adult rider. */
export function buildAdventureScooter(scene: Scene, withRider = true): AdventureActor {
  const s=new Sculpt(scene,'adventureScooter');
  const paint=s.material('oxideRedEnamel','#A13F25',.28,.18),cream=s.material('warmBodyTrim','#D6CCAB',.38,.12);
  const rubber=s.material('tireRubber','#202322',.94),saddle=s.material('saddleLeather','#3A3027',.71);
  const metal=s.material('chrome','#B2B9B3',.23,.85),dark=s.material('mechanical','#4C5653',.55,.6);
  const headlamp=s.material('headlampGlass','#E9E2BD',.18);headlamp.emissiveColor=Color3.FromHexString('#4A4122');
  const rear=s.material('rearLens','#BE321D',.27);rear.emissiveColor=Color3.FromHexString('#3F1103');
  const wheels:TransformNode[]=[];
  for(const z of [-.55,.59]){const w=s.joint('wheel',[0,.252,z]);wheels.push(w);
    const tire=CreateTorus('scooterTire',{diameter:.41,thickness:.10,tessellation:32},scene);tire.rotation.z=Math.PI/2;s.add(tire,rubber,[0,0,0],w);
    const hub=CreateCylinder('alloyHub',{diameter:.31,height:.087,tessellation:32},scene);hub.rotation.z=Math.PI/2;s.add(hub,dark,[0,0,0],w);
    for(const side of [-1,1]){const ring=CreateTorus('wheelRim',{diameter:.31,thickness:.015,tessellation:24},scene);ring.rotation.z=Math.PI/2;s.add(ring,metal,[side*.05,0,0],w);
      for(let i=0;i<8;i++){const a=i/8*Math.PI*2;s.rod('castSpoke',[side*.052,Math.cos(a)*.055,Math.sin(a)*.055],[side*.052,Math.cos(a)*.147,Math.sin(a)*.147],.008,.013,metal,w);}
    }
  }
  s.form('rearBodywork',[[.30,.105,.22,-.43],[.40,.185,.29,-.40],[.55,.218,.30,-.37],[.65,.211,.31,-.35],[.70,.173,.28,-.34]],paint,[0,0,0],s.root,3.1,40);
  s.block('saddle',[.40,.109,.72],[0,.745,-.24],saddle,s.root,.035);
  s.block('saddlePiping',[.401,.011,.699],[0,.772,-.24],cream,s.root,.003);
  s.block('floorboard',[.374,.066,.535],[0,.326,.165],dark,s.root,.015);
  for(let i=0;i<5;i++)s.block('floorGrip',[.23,.006,.016],[0,.365,-.02+i*.06],rubber,s.root,.002);
  s.form('legShield',[[.33,.134,.052,.37],[.43,.195,.064,.40],[.67,.223,.05,.49],[.88,.16,.052,.56],[.95,.106,.059,.59]],paint,[0,0,0],s.root,3.2,40);
  s.form('innerShield',[[.39,.135,.015,.338],[.6,.193,.023,.42],[.84,.15,.025,.512]],cream,[0,0,0],s.root,3.2);
  for(const x of [-.078,.078])s.rod('frontFork',[x,.26,.59],[x,.86,.575],.019,.026,metal);
  s.form('frontMudguard',[[.424,.065,.17,.59],[.487,.095,.195,.59],[.528,.057,.126,.59]],paint,[0,0,0],s.root,2.6);
  s.block('handlebarHousing',[.334,.139,.151],[0,.984,.604],paint,s.root,.045);
  s.oval('glassHeadlight',[.238,.084,.023],[0,.991,.68],headlamp);
  s.rod('handlebar',[-.314,.986,.576],[.314,.986,.576],.014,.014,metal);
  for(const side of [-1,1]) {s.rod('rubberGrip',[side*.222,.989,.576],[side*.321,.989,.576],.022,.022,rubber);
    s.curve('mirrorStalk',[[side*.2,1.012,.615],[side*.25,1.15,.621],[side*.31,1.21,.64]],.009,metal);
    s.oval('mirrorBack',[.106,.068,.028],[side*.31,1.222,.64],dark);s.oval('mirrorGlass',[.09,.053,.008],[side*.31,1.222,.623],metal);
    s.rod('rearSuspension',[side*.129,.287,-.54],[side*.14,.625,-.39],.019,.019,metal);
  }
  s.block('rearLamp',[.245,.067,.028],[0,.599,-.672],rear,s.root,.012);
  s.block('plate',[.131,.102,.014],[0,.451,-.688],cream,s.root,.005);
  s.rod('exhaust',[.19,.295,-.09],[.205,.303,-.675],.037,.044,dark);
  s.rod('exhaustTip',[.205,.303,-.674],[.205,.303,-.713],.037,.035,metal);
  s.curve('luggageRail',[[-.18,.767,-.51],[-.18,.79,-.7],[.18,.79,-.7],[.18,.767,-.51]],.013,metal);
  if(withRider) {
    const jeans=s.material('riderDenim','#283733',.93),jacket=s.material('riderJacket','#333934',.91);
    const skin=s.material('riderSkin','#B58B66',.71),helmet=s.material('helmetEnamel','#C76A33',.3,.08),strap=s.material('helmetTrim','#292C25',.77);
    const rider=s.joint('rider',[0,0,-.08]);
    s.form('riderTorso',[[.79,.15,.109],[.92,.176,.119],[1.08,.205,.135,.044],[1.26,.208,.102,.073],[1.31,.075,.065,.06]],jacket,[0,0,0],rider,2.5,36);
    s.rod('neck',[0,1.285,.065],[0,1.405,.087],.053,.047,skin,rider);
    s.oval('riderHead',[.183,.23,.19],[0,1.433,.1],skin,rider,18);
    s.form('helmetShell',[[1.428,.094,.096,.087],[1.473,.113,.115,.087],[1.555,.096,.101,.087],[1.589,.055,.06,.087],[1.6,.008,.009,.087]],helmet,[0,0,0],rider,2,40);
    s.form('helmetRim',[[1.424,.097,.099,.087],[1.437,.103,.103,.087]],strap,[0,0,0],rider);
    s.block('helmetVisorBrim',[.21,.022,.15],[0,1.474,.195],helmet,rider,.009);
    s.curve('helmetStrap',[[-.088,1.44,.08],[-.065,1.348,.127],[0,1.326,.143],[.065,1.348,.127],[.088,1.44,.08]],.008,strap,rider);
    for(const side of [-1,1]) {
      s.rod('jacketUpperSleeve',[side*.19,1.23,.064],[side*.265,1.077,.29],.065,.055,jacket,rider);
      s.oval('elbow',[.11,.13,.115],[side*.265,1.077,.29],jacket,rider);
      s.rod('jacketForearm',[side*.265,1.077,.29],[side*.273,1.015,.615],.055,.036,jacket,rider);
      s.oval('grippingHand',[.067,.056,.096],[side*.274,1.006,.646],skin,rider);
      s.rod('riderThigh',[side*.13,.798,-.13],[side*.176,.72,.29],.096,.086,jeans,rider);
      s.oval('riderKnee',[.164,.15,.155],[side*.176,.72,.29],jeans,rider);
      s.rod('riderCalf',[side*.176,.705,.29],[side*.173,.407,.108],.082,.054,jeans,rider);
      s.block('riderShoe',[.13,.093,.262],[side*.173,.409,.16],strap,rider,.025);
      s.curve('jacketSeam',[[side*.13,.89,.084],[side*.13,1.11,.154],[side*.10,1.28,.13]],.003,s.material('jacketStitch','#586055',.91),rider,5);
    }
  }
  let lastTime: number | undefined, travelled = 0;
  return s.finish((time,speed)=>{const dt=lastTime===undefined?0:Math.max(0,Math.min(.1,time-lastTime));lastTime=time;travelled+=dt*speed;for(const w of wheels)w.rotation.x=travelled/.252;});
}
